import React, { useState, useEffect } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import styles from "../Dashboard/offtime/offtime.module.css";
import { useSelector } from "react-redux";

const DatePickerCalendar = ({ setIsOpen, currentDate, setSelectedDate }) => {
  const initialDate = currentDate instanceof Date ? currentDate : new Date(); 
  const [dropdownCalendarDate, setDropdownCalendarDate] = useState(initialDate);
  const { theme } = useSelector((state) => state.theme);
  const [days, setDays] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const isToday = (year, month, day) => {
    const today = new Date();
    return (
      year === today.getFullYear() &&
      month === today.getMonth() &&
      day === today.getDate()
    );
  };

  const generateCalendar = (date) => {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const lastDayOfPrevMonth = new Date(date.getFullYear(), date.getMonth(), 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const daysInMonth = [];
    const startDay = firstDayOfMonth.getDay();

    // Previous month's days
    for (let i = startDay - 1; i >= 0; i--) {
      const dayDate = new Date(date.getFullYear(), date.getMonth() - 1, lastDayOfPrevMonth - i);
      daysInMonth.push({
        day: lastDayOfPrevMonth - i,
        currentMonth: false,
        isPastDate: dayDate < today,
        date: dayDate
      });
    }

    // Current month's days
    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const dayDate = new Date(date.getFullYear(), date.getMonth(), i);
      daysInMonth.push({
        day: i,
        currentMonth: true,
        isPastDate: dayDate < today,
        date: dayDate
      });
    }

    // Next month's days
    const totalCells = 42; // 6 weeks to handle edge cases
    const remainingDays = totalCells - daysInMonth.length;
    for (let i = 1; i <= remainingDays; i++) {
      const dayDate = new Date(date.getFullYear(), date.getMonth() + 1, i);
      daysInMonth.push({
        day: i,
        currentMonth: false,
        isPastDate: dayDate < today,
        date: dayDate
      });
    }

    setDays(daysInMonth);
  };

  const goToPrevMonth = (e) => {
    e.stopPropagation();
    const prevMonth = new Date(dropdownCalendarDate.getFullYear(), dropdownCalendarDate.getMonth() - 1, 1);
    setDropdownCalendarDate(prevMonth);
  };

  const goToNextMonth = (e) => {
    e.stopPropagation();
    const nextMonth = new Date(dropdownCalendarDate.getFullYear(), dropdownCalendarDate.getMonth() + 1, 1);
    setDropdownCalendarDate(nextMonth);
  };

  const handleDateClick = (e, day, isCurrentMonth) => {
    e.stopPropagation();
    if (!isCurrentMonth) return;

    const selectedDate = formatDate(
      new Date(dropdownCalendarDate.getFullYear(), dropdownCalendarDate.getMonth(), day)
    );

    setSelectedDate(selectedDate);

    setSelectedDates((prevDates) =>
      prevDates.includes(selectedDate)
        ? prevDates.filter((date) => date !== selectedDate)
        : [...prevDates, selectedDate]
    );
    setIsOpen(false);
  };

  useEffect(() => {
    generateCalendar(dropdownCalendarDate);
  }, [dropdownCalendarDate]);

  useEffect(() => {
    setSelectedDate(selectedDates);
  }, [selectedDates, setSelectedDate]);

  const currentDay = new Date();
  const currentMonthYear = `${currentDay.toLocaleString("default", {
    month: "long",
  })} ${currentDay.getFullYear()}`;
  const currentDaySequence = `${currentDay.getDate()}${['st','nd','rd'][((currentDay.getDate()+90)%100-10)%10-1]||'th'}`;

  return (
    <div
      className={`rounded-[20px]  w-full max-w-md p-6 border shadow-lg ${styles.calendarContainer} ${styles.modal} ${theme === 'dark' ? 'bg-[#212529] border-[#555657]' : "bg-[#F2F4F7] border-[#9A9FA5]"}`}
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">
          {dropdownCalendarDate.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </h2>
        <div className="flex gap-4 items-center">
          <button onClick={goToPrevMonth}>
            <FaChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={goToNextMonth}>
            <FaChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-center text-sm font-medium mb-2">
        {daysOfWeek.map((day) => (
          <div key={day} className="p-1">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 text-center">
        {days.map((dayObj, index) => {
          const { day, currentMonth, isPastDate, date } = dayObj;
          const formattedDate = formatDate(date);
          const isSelected = selectedDates.includes(formattedDate);
          const highlightToday = isToday(date.getFullYear(), date.getMonth(), date.getDate());

          return (
            <div
              key={index}
              onClick={(e) => handleDateClick(e, day, currentMonth)}
              className={`p-2 flex items-center justify-center rounded-full transition-all ${
                currentMonth
                  ? theme === "dark"
                    ? "text-white"
                    : "text-black"
                  : theme === "dark"
                  ? "text-gray-400"
                  : "text-gray-600"
              } ${
                isSelected ? "bg-[#CC0927] text-white" : ""
              } ${
                highlightToday && currentMonth && !isSelected
                  ? "border-2 border-[#CC0927]"
                  : ""
              } hover:bg-[#CC0927] hover:text-white cursor-pointer`}
            >
              {day}
            </div>
          );
        })}
      </div>

      <div className="text-center mt-6">
        <p className="text-sm opacity-80">
          Today is <strong>{currentMonthYear}</strong>, the <strong>{currentDaySequence}</strong> day of the month.
        </p>
      </div>
    </div>
  );
};

export default DatePickerCalendar;
